// Web Audio engine for healing frequencies + ambient layers
// Singleton-ish so multiple components share state cleanly.

const ARTWORK_BASE = (typeof window !== 'undefined' && window.location)
  ? window.location.origin
  : '';

// Dev-only error visibility for the many catch blocks below. Most audio
// failures are "node already stopped" / "context not yet unlocked" — they
// can't be remediated at the call site so we deliberately swallow them. But
// in development we still want them showing in the console so genuine bugs
// don't hide. In production the warning becomes a no-op (zero overhead).
const _DEV = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';
function _warn(ctx, err) {
  if (_DEV && err) {
    console.warn(`[audioEngine] ${ctx}:`, err && err.message ? err.message : err);
  }
}

// HF-037 Android audio optimizations. Mid-range Android devices routinely
// distort or chop when the Web Audio graph asks for anything above the
// hardware sample rate (48 kHz on most modern Androids, 44.1 kHz on
// budget/older devices) — the browser resamples on-the-fly and the audio
// thread can't keep up. We also give the OS driver a little headroom
// beneath unity because Android's mixer clips earlier than iOS's.
const _IS_ANDROID = typeof navigator !== 'undefined'
  && /android/i.test(navigator.userAgent || '');
// Master ceiling: 1.0 on desktop/iOS (transparent), 0.85 on Android so the
// summed peak (tone + ambient + isochronic gate) has ~1.4 dB of headroom
// beneath the OS mixer's clip point. Tuned empirically — lower kills the
// perceived "presence" of the tone, higher lets the buzzy distortion
// leak back in on Pixel/Samsung mid-range hardware.
const _MASTER_CEILING = _IS_ANDROID ? 0.85 : 1.0;
// HF-038: Android startup floor. The first ~250 ms of playback is when
// the audio thread is most fragile — buffer allocation + oscillator
// scheduling + noise-buffer render can all coincide inside a single
// render quantum and clip. We open at 0.65 (~−3.7 dB) on Android and
// ramp up to the ceiling AFTER we've confirmed ctx.state === 'running',
// giving the driver breathing room during the exact window when it's
// most likely to distort.
const _MASTER_STARTUP_FLOOR = _IS_ANDROID ? 0.65 : 1.0;
// Scheduling lead-time before an oscillator .start() call. Desktop hits
// 50 ms comfortably; Android needs ~120 ms so the audio thread can pack
// the buffer without underrunning between the JS-thread schedule call
// and the audio-thread render deadline.
const _SCHEDULE_LEAD_MS = _IS_ANDROID ? 120 : 50;
// HF-038: pre-ramp settle window on Android. When AudioContext transitions
// from 'suspended'|'interrupted' → 'running' the audio clock advances a
// few tens of ms in a burst — starting the master fade-in ramp inside
// that burst produces an audible 'tick' or brief stutter. Waiting one
// render quantum (128 samples at 48 kHz ≈ 2.7 ms) plus a driver-side
// commit slack is the sweet spot. Zero on desktop/iOS which resume cleanly.
const _RESUME_SETTLE_MS = _IS_ANDROID ? 30 : 0;

/**
 * HF-038: probe the browser's default AudioContext to discover the exact
 * hardware sample rate the OS mixer is using. Creating a throwaway context
 * with no options resolves the "native" rate (48000 on most Android and
 * Chrome/Edge, 44100 on some older Android + Firefox). We then feed that
 * exact value into the real context so no on-the-fly resampling ever
 * happens — the largest single cause of Android choppiness.
 *
 * Returns null if the probe fails (Safari private mode / very old browser),
 * which lets the caller fall back to the multi-tier attempts list.
 */
function _probeHardwareSampleRate() {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  let probe = null;
  try {
    probe = new Ctor();
    const rate = probe.sampleRate;
    return typeof rate === 'number' && rate > 0 ? rate : null;
  } catch (_) {
    return null;
  } finally {
    if (probe && typeof probe.close === 'function') {
      try { probe.close(); } catch (_) { /* graceful */ }
    }
  }
}

class AudioEngine {
  constructor() {
    this.ctx = null;
    this.master = null;

    // Tone (oscillator) state
    this.osc = null;
    this.oscR = null; // for binaural
    this.phiOscs = []; // [{osc, gain}, ...] for golden-stack harmonics
    this.toneGain = null;
    this.frequency = 432;
    this.waveform = 'sine';
    this.binaural = 0; // hz offset for right ear
    this.toneVolume = 0.35;
    this.goldenStack = false; // when true, layer tones at f×φ and f×φ²
    this.isochronic = 0; // Hz pulse rate (0 = off). True isochronic: carrier
                         // amplitude is square-wave gated between 0 and 1 at
                         // this rate, no phase relationship with binaural.

    // Isochronic gate plumbing: a Gain node sitting between the oscillator(s)
    // and toneGain. Its .gain is driven by a square LFO scaled to 0..1 when
    // isochronic > 0, or held at constant 1 when off.
    this.gateGain = null;
    this.isoLfo = null;
    this.isoOffset = null;
    this.isoScale = null;
    this.isoSmoother = null;
    this.isoFadeIn = null;

    // Auto-recovery mutex. `_recoveryInProgress` is set true only while
    // `_postStartHealthCheck` is actively rebuilding the graph after a
    // detected failure. `_recoveryAborted` is set by manual stop()/start()
    // during that window so the recovery routine bails out and lets the
    // user's intent win. Together they prevent the "auto-recovery starts
    // audio right after the user tapped stop" race.
    this._recoveryInProgress = false;
    this._recoveryAborted = false;

    // Analyser for real-time visualizers (Chladni, ripples). Created lazily
    // and tapped off the master bus so it sees everything (tone + ambient).
    this.analyser = null;
    this._analyserData = null;

    // Ambient layers: { rain, ocean, forest } -> { node, gain }
    this.ambient = {};

    // Background-playback plumbing:
    //  _streamDest = MediaStreamAudioDestinationNode — re-routes the master mix
    //                into a MediaStream that an <audio> element can consume.
    //  _sinkEl     = hidden HTMLAudioElement playing that stream. Because the
    //                browser sees it as "media playback", iOS Safari + Android
    //                Chrome keep the audio thread alive when the screen locks
    //                or the tab is backgrounded. Without this, procedural
    //                Web Audio is suspended on screen-lock and the user has to
    //                tap play again on resume.
    this._streamDest = null;
    this._sinkEl = null;
    this._wakeLock = null;        // Screen Wake Lock sentinel (opt-in only)
    this._wakeLockWanted = false; // user's "keep screen awake" preference
    this._mediaTitle = 'Healing Frequencies';
    this._mediaSubtitle = '';
    this._mediaSessionBound = false;

    this.playing = false;
    // Isochronic background-freeze state — flipped true by
    // freezeIsochronicForBackground() when the tab hides while the user has
    // isochronic pulse enabled. resumeIsochronicAfterBackground() rebuilds
    // the LFO on visibility return. Constructor-initialized (rather than
    // class-field syntax) for stylistic consistency with the rest of the file.
    this._isoFrozen = false;
    this._isoRateBeforeHide = 0;
    // Wall-clock timestamp (ms since epoch) at which the current session's
    // Smart-Fade timer is scheduled to hit 00:00. Kept on the singleton
    // (not React state) so Dashboard.jsx can restore the correct
    // countdown after remounting — e.g. when the user visits /account
    // mid-session and comes back. `0` means "no active session countdown".
    this.sessionEndAt = 0;
    this.listeners = new Set();
  }

  async _ensureCtx() {
    let created = false;
    if (!this.ctx) {
      // HF-040 Android overhaul:
      //   - No latencyHint on Android. `'playback'` requests a large
      //     buffer that causes underruns (choppiness) on mid-range
      //     Android hardware. Omitting it lets the browser pick the
      //     driver-preferred latency profile, which on Android is
      //     usually the small "interactive" buffer.
      //   - No forced sampleRate on Android. Forcing an explicit rate
      //     (even the probed hardware rate) triggers the very
      //     resampling path we were trying to avoid on some devices —
      //     the browser will re-open the driver at a different rate
      //     than the constructor requested and resample. Constructing
      //     with defaults lets the browser adopt the driver's native
      //     rate directly. We still stash `ctx.sampleRate` on the
      //     instance for the fidelity read-out.
      //   Desktop / iOS retain the pre-HF-040 behaviour (they benefit
      //   from the higher-rate + playback-latency pair).
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (_IS_ANDROID) {
        try { this.ctx = new Ctor(); } catch (e) { this.ctx = null; }
        this._hardwareSampleRate = this.ctx ? this.ctx.sampleRate : null;
      } else {
        let attempts = [96000, 48000];
        this._hardwareSampleRate = null;
        for (const sr of attempts) {
          try {
            this.ctx = new Ctor({ sampleRate: sr, latencyHint: 'playback' });
            if (this.ctx) break;
          } catch (e) { /* try next */ this.ctx = null; }
        }
        if (!this.ctx) {
          try { this.ctx = new Ctor({ latencyHint: 'playback' }); } catch (e) { this.ctx = null; }
        }
      }
      if (!this.ctx) this.ctx = new Ctor();
      // HF-037/038: on Android we OPEN the master bus at the startup floor
      // (0.65 ≈ −3.7 dB) so the audio thread has clipping headroom during
      // the fragile first ~250 ms of playback. Once ctx.state is confirmed
      // 'running' and the settle window elapses, we ramp up to the ceiling
      // (0.85 ≈ −1.4 dB). Desktop/iOS opens at true unity.
      this.master = this.ctx.createGain();
      this.master.gain.value = _MASTER_STARTUP_FLOOR;
      // HF-040 limiter: DynamicsCompressor acting as a brick-wall limiter
      // catches transient peaks that the static master ceiling can't. Sums
      // of tone + φ harmonics + ambient layers can briefly exceed unity
      // even when each source is nominally below its individual ceiling —
      // the limiter clamps those transients before they reach the OS
      // mixer's clip point. Placed BETWEEN master and destination so
      // every downstream sink (destination + streamDest) sees the
      // limited signal. Analyser stays tapped off master so cymatics
      // visualisations track the raw mix, not the compressed envelope.
      // Params tuned per HF-040 spec: -3 dB threshold, 12:1 ratio, fast
      // attack (3 ms) to catch transients, short release (100 ms) so the
      // limiter unclamps quickly between peaks and doesn't audibly duck.
      // 0 dB knee = hard-knee limiting (no smooth transition into
      // compression) — we want a firm ceiling, not gentle compression.
      this._limiter = this.ctx.createDynamicsCompressor();
      try {
        this._limiter.threshold.value = -3;
        this._limiter.knee.value = 0;
        this._limiter.ratio.value = 12;
        this._limiter.attack.value = 0.003;
        this._limiter.release.value = 0.1;
      } catch (e) { _warn('audio.limiter.params', e); }
      this.master.connect(this._limiter);
      this._limiter.connect(this.ctx.destination);
      // Bind statechange BEFORE anything else. This is the ONLY event that
      // fires reliably when the OS suspends/resumes the audio thread on
      // mobile (visibilitychange lags behind and may arrive after the
      // graph is already frozen). Our handler synchronously disconnects
      // the isochronic LFO on suspend and rebuilds it on resume — so no
      // pulse-phase drift can leak into the audible output during a
      // screen-lock cycle.
      try {
        this.ctx.addEventListener('statechange', () => this._onCtxStateChange());
      } catch (e) { _warn('audio.statechange', e); }
      created = true;
    }
    if (this.ctx.state !== 'running') {
      try { await this.ctx.resume(); } catch (e) { _warn('audio', e); }
    }
    // iOS Safari + Chrome-on-iOS need a 1-sample silent BufferSource to fully unlock
    // WebAudio. resume() alone is not always sufficient. Idempotent via this._unlocked.
    if (!this._unlocked) {
      try {
        const buf = this.ctx.createBuffer(1, 1, 22050);
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        src.connect(this.ctx.destination);
        if (typeof src.start === 'function') src.start(0);
        else if (typeof src.noteOn === 'function') src.noteOn(0);
        this._unlocked = true;
      } catch (e) { _warn('audio', e); }
    }
    // Build the background-playback sink ONCE per ctx. Routes the master mix
    // through an <audio> element so the OS treats the page as media playback.
    // Critical for lock-screen / backgrounded continuation on iOS + Android.
    if (created) {
      this._buildBackgroundSink();
      this._buildAnalyser();
      this._prebuildAllAmbient();
      // Re-apply any persisted hearing profile from prior session AFTER the
      // background sink + analyser are wired, so the EQ insertion knows about
      // every downstream destination it needs to detour through the chain.
      if (this._pendingProfile) {
        const p = this._pendingProfile;
        this._pendingProfile = null;
        this._applyEqChain(p);
      }
      // HF-038: on Android, ramp the master bus from the startup floor
      // (0.65) up to the ceiling (0.85) AFTER the audio clock has had a
      // chance to settle. `_settleMasterAfterCreate` waits for the settle
      // window (one render quantum + driver commit slack) before scheduling
      // the ramp so the first audible tick happens on a synchronized clock,
      // not mid-burst as ctx.state flips to 'running'.
      if (_IS_ANDROID && this.master) {
        this._settleMasterAfterCreate();
      }
    }
    return this.ctx;
  }

  /**
   * HF-038: post-context-creation settle ramp for Android. Waits
   * `_RESUME_SETTLE_MS` (default 30 ms on Android) so the audio clock has
   * committed at least one render quantum, THEN linearly ramps master
   * from the startup floor (0.65) up to the ceiling (0.85) over 250 ms.
   * The 250 ms window is long enough to be inaudible as a fade but short
   * enough to hit the target before the user perceives any attenuation.
   *
   * If a later start() overrides master.gain via cancelScheduledValues
   * + setValueAtTime(_MASTER_CEILING), that write wins and we're
   * automatically at the ceiling — no cleanup needed here.
   */
  _settleMasterAfterCreate() {
    setTimeout(() => {
      if (!this.ctx || !this.master) return;
      // Only lift if we're still parked at the startup floor. If the user
      // already tapped Play (which snaps master to the ceiling) or if a
      // smart-fade has taken over, we bail so we don't fight a scheduled
      // ramp mid-flight.
      const cur = this.master.gain.value;
      if (Math.abs(cur - _MASTER_STARTUP_FLOOR) > 0.02) return;
      try {
        const t = this.ctx.currentTime;
        this.master.gain.cancelScheduledValues(t);
        this.master.gain.setValueAtTime(cur, t);
        this.master.gain.linearRampToValueAtTime(_MASTER_CEILING, t + 0.25);
      } catch (e) { _warn('audio.settle', e); }
    }, _RESUME_SETTLE_MS);
  }

  // ---- Hearing-profile EQ chain ------------------------------------------
  // Inserts a series of peaking BiquadFilterNodes between the master gain
  // and ctx.destination (+ streamDest + analyser). The chain compensates
  // for a user's audiogram: bands they couldn't hear during onboarding get
  // a +6 dB peaking boost; bands they heard stay flat. Q is moderate (1.4)
  // so neighbouring bands blend smoothly into a continuous response curve.
  //
  // setHearingProfile(profile|null) is idempotent — calling with null tears
  // the chain down and reconnects master directly to all destinations.
  // Safe to call before _ensureCtx (deferred via _pendingProfile).
  setHearingProfile(profile) {
    if (!this.ctx) {
      // ctx not built yet — defer until first start(). _ensureCtx will pick
      // this up after wiring sinks. Avoids creating an unused AudioContext.
      this._pendingProfile = profile;
      this._lastProfile = profile;
      return;
    }
    this._lastProfile = profile;
    this._applyEqChain(profile);
  }

  _applyEqChain(profile) {
    // Tear down any existing chain first so re-calibration doesn't stack
    // multiple EQs on top of each other.
    this._clearEqChain();
    if (!profile || !Array.isArray(profile.bands) || profile.bands.length === 0) return;

    const ctx = this.ctx;
    try {
      const filters = [];
      profile.bands.forEach((b) => {
        const gainDb = Number(b.gain_db) || 0;
        // Skip flat bands — saves CPU on a typical "heard everything" profile.
        if (Math.abs(gainDb) < 0.05) return;
        const f = ctx.createBiquadFilter();
        f.type = 'peaking';
        f.frequency.value = b.freq;
        f.Q.value = 1.4;
        f.gain.value = Math.max(-9, Math.min(9, gainDb));
        filters.push(f);
      });
      if (filters.length === 0) return; // entirely-flat profile = no-op

      // HF-040: EQ chain sits BETWEEN master and limiter so the personal
      // EQ boost feeds a clip-protected sum. Master no longer connects
      // directly to the limiter — it routes through the filter chain,
      // then the chain's tail feeds the limiter, which feeds destination
      // and the background sink.
      try { this.master.disconnect(); } catch (e) { /* not connected yet */ }
      this.master.connect(filters[0]);
      for (let i = 0; i < filters.length - 1; i++) {
        filters[i].connect(filters[i + 1]);
      }
      const tail = filters[filters.length - 1];
      if (this._limiter) {
        try { tail.connect(this._limiter); } catch (e) { _warn('audio', e); }
      } else {
        // Fallback: no limiter (shouldn't happen post-HF-040). Wire
        // straight to destination + streamDest as before.
        tail.connect(ctx.destination);
        if (this._streamDest) {
          try { tail.connect(this._streamDest); } catch (e) { _warn('audio', e); }
        }
      }
      if (this.analyser) {
        // Re-attach the analyser to the master so the visualizer measures the
        // raw mix (without the personal EQ boost biasing the amplitude).
        try { this.master.connect(this.analyser); } catch (e) { _warn('audio', e); }
      }
      this._eqFilters = filters;
    } catch (e) {
      _warn('audio', e);
    }
  }

  _clearEqChain() {
    if (!this._eqFilters || this._eqFilters.length === 0) return;
    try { this.master.disconnect(); } catch (e) { /* */ }
    this._eqFilters.forEach((f) => { try { f.disconnect(); } catch (e) { /* */ } });
    this._eqFilters = [];
    // HF-040: restore canonical routing = master → limiter (limiter still
    // wired to destination + streamDest from _ensureCtx / _buildBackgroundSink).
    // Only if the limiter is missing do we fall back to a direct connection.
    if (this._limiter) {
      try { this.master.connect(this._limiter); } catch (e) { _warn('audio', e); }
    } else {
      try { this.master.connect(this.ctx.destination); } catch (e) { _warn('audio', e); }
      if (this._streamDest) {
        try { this.master.connect(this._streamDest); } catch (e) { _warn('audio', e); }
      }
    }
    if (this.analyser) {
      try { this.master.connect(this.analyser); } catch (e) { _warn('audio', e); }
    }
  }

  // One-shot calibration tone. Plays `freq` for `durationMs` at `gainDb`
  // relative to unity (so -30 means 0.0316 amplitude). Returns a promise
  // that resolves when the tone has finished. Used by the calibration
  // modal to test each audiogram band. Bypasses the EQ chain so the test
  // measures the user's bare hearing, not a self-amplified loop.
  async playCalibrationTone(freq, durationMs = 2000, gainDb = -30) {
    await this._ensureCtx();
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = Math.max(20, Math.min(20000, Number(freq) || 1000));
    const g = ctx.createGain();
    g.gain.value = 0;
    osc.connect(g).connect(ctx.destination); // direct → bypass EQ + master
    const targetLinear = Math.pow(10, gainDb / 20);
    const fadeMs = 80;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(targetLinear, now + fadeMs / 1000);
    g.gain.setValueAtTime(targetLinear, now + (durationMs - fadeMs) / 1000);
    g.gain.linearRampToValueAtTime(0, now + durationMs / 1000);
    osc.start(now);
    osc.stop(now + (durationMs / 1000) + 0.05);
    return new Promise((resolve) => {
      osc.onended = () => {
        try { osc.disconnect(); g.disconnect(); } catch (e) { /* */ }
        resolve();
      };
    });
  }

  // Real-time AnalyserNode tapped off master gain. Drives the cymatics
  // visualizer. ~1024 fft = good resolution without blowing CPU on mobile.
  _buildAnalyser() {
    if (this.analyser || !this.ctx) return;
    try {
      const a = this.ctx.createAnalyser();
      a.fftSize = 1024;
      a.smoothingTimeConstant = 0.82;
      this.master.connect(a);
      this.analyser = a;
      this._analyserData = new Uint8Array(a.frequencyBinCount);
    } catch (e) { _warn('audio', e); }
  }

  // Public: per-frame snapshot of audio amplitude (0..1) for visualizers.
  // Cheap — reuses the same Uint8Array buffer.
  getAmplitude() {
    if (!this.analyser) return 0;
    try {
      this.analyser.getByteTimeDomainData(this._analyserData);
      // RMS around 128 (DC bias for unsigned-byte time-domain data).
      let sum = 0;
      const N = this._analyserData.length;
      for (let i = 0; i < N; i++) {
        const v = (this._analyserData[i] - 128) / 128;
        sum += v * v;
      }
      return Math.sqrt(sum / N);
    } catch (e) { return 0; }
  }

  // Pipe master gain into a MediaStream → <audio> element. Some browsers
  // (notably older iOS) don't support MediaStreamAudioDestinationNode; in that
  // case we silently fall back to ctx.destination only.
  _buildBackgroundSink() {
    if (this._streamDest || !this.ctx) return;
    try {
      if (typeof this.ctx.createMediaStreamDestination !== 'function') return;
      const dest = this.ctx.createMediaStreamDestination();
      // HF-040: route the background sink OFF THE LIMITER so the mobile
      // media stream gets the same clip-protected mix that ctx.destination
      // gets. Falls back to master if the limiter somehow wasn't built.
      const src = this._limiter || this.master;
      src.connect(dest);
      const el = document.createElement('audio');
      el.setAttribute('data-role', 'audio-engine-sink');
      el.setAttribute('playsinline', '');
      el.setAttribute('x-webkit-playsinline', '');
      el.setAttribute('webkit-playsinline', '');
      el.preload = 'auto';
      el.crossOrigin = 'anonymous';
      el.muted = false;
      el.autoplay = false;
      // Keep it visually hidden but DON'T use display:none — some browsers
      // optimise display:none media elements out of the background-audio
      // scheduler. A 1×1 transparent off-screen element is the safe choice.
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      el.style.top = '0';
      el.style.width = '1px';
      el.style.height = '1px';
      el.style.opacity = '0';
      el.style.pointerEvents = 'none';
      el.srcObject = dest.stream;
      document.body.appendChild(el);
      this._streamDest = dest;
      this._sinkEl = el;
    } catch (e) { /* unsupported — fall back to ctx.destination */ }
  }

  // ---- Media Session (lock-screen metadata + transport controls) ------------
  _bindMediaSession() {
    if (this._mediaSessionBound) return;
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.setActionHandler('play', () => { this.start(); });
      navigator.mediaSession.setActionHandler('pause', () => { this.stop(); });
      navigator.mediaSession.setActionHandler('stop', () => { this.stop(); });
      // 'previoustrack' / 'nexttrack' intentionally not bound — single-stream app.
      this._mediaSessionBound = true;
    } catch (e) { /* some action handlers may be unsupported */ }
  }

  _updateMediaSession() {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    try {
      const title = this._mediaSubtitle
        ? `${this._mediaTitle} · ${this._mediaSubtitle}`
        : this._mediaTitle;
      const MM = window.MediaMetadata;
      if (MM) {
        navigator.mediaSession.metadata = new MM({
          title,
          artist: 'Solarisound',
          album: 'Healing Frequencies',
          artwork: [
            { src: `${ARTWORK_BASE}/icon-192.png`, sizes: '192x192', type: 'image/png' },
            { src: `${ARTWORK_BASE}/icon-512.png`, sizes: '512x512', type: 'image/png' },
          ],
        });
      }
      navigator.mediaSession.playbackState = this.playing ? 'playing' : 'paused';
    } catch (e) { _warn('audio', e); }
  }

  // Public: let the UI tell the engine what to display on the lock-screen.
  // `subtitle` is the human-friendly preset name (e.g. "528 Hz · Love").
  setMediaInfo({ title, subtitle } = {}) {
    if (typeof title === 'string') this._mediaTitle = title;
    if (typeof subtitle === 'string') this._mediaSubtitle = subtitle;
    if (this.playing) this._updateMediaSession();
  }

  // ---- Wake Lock (opt-in: keeps the screen on while playing) ----------------
  async requestWakeLock() {
    this._wakeLockWanted = true;
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return false;
    if (this._wakeLock) return true;
    try {
      const sentinel = await navigator.wakeLock.request('screen');
      this._wakeLock = sentinel;
      sentinel.addEventListener('release', () => {
        // OS released it (tab hidden, low battery, etc.). Clear ref so we can
        // re-acquire on next visibility change if the user still wants it.
        if (this._wakeLock === sentinel) this._wakeLock = null;
      });
      return true;
    } catch (e) {
      return false;
    }
  }

  async releaseWakeLock() {
    this._wakeLockWanted = false;
    if (!this._wakeLock) return;
    try { await this._wakeLock.release(); } catch (e) { _warn('audio', e); }
    this._wakeLock = null;
  }

  // Called by Dashboard on visibilitychange so the lock can be re-acquired
  // after the OS auto-released it on tab switch / lock screen.
  async reacquireWakeLockIfWanted() {
    if (!this._wakeLockWanted || this._wakeLock) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    await this.requestWakeLock();
  }

  wakeLockSupported() {
    return typeof navigator !== 'undefined' && 'wakeLock' in navigator;
  }
  wakeLockActive() { return !!this._wakeLock; }
  wakeLockWanted() { return this._wakeLockWanted; }

  // Warm-up: call from the first user gesture on the page to unlock audio on iOS/Safari
  // BEFORE the user taps a specific frequency. Safe to call multiple times.
  async unlock() {
    try { await this._ensureCtx(); } catch (e) { _warn('audio', e); }
  }

  // Pre-build every ambient layer at the SAME audio-context time so that all loops
  // are sample-accurately aligned for the entire session — no drift between layers.
  // Each source has gain=0 until the user opens a slider, so this is silent until used.
  //
  // HF-037: on Android we skip the eager prebuild entirely. Eight looping
  // noise BufferSources + four LFOs firing forever burn ~5-8% of the audio
  // thread's budget even at gain=0 (they're still rendered into the mix),
  // enough to trigger buffer underruns on mid-range hardware. Instead we
  // lazily build each layer the first time setAmbient() is called for it
  // — the fallback path already handles this cleanly.
  _prebuildAllAmbient() {
    if (_IS_ANDROID) return;
    const kinds = ['rain', 'ocean', 'forest', 'wind', 'crickets', 'bowls', 'brown', 'white'];
    const startAt = this.ctx.currentTime + 0.05;
    kinds.forEach((kind) => {
      if (this.ambient[kind]) return;
      this.ambient[kind] = this._buildAmbient(kind, startAt);
    });
  }

  on(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  _emit() { this.listeners.forEach((f) => f(this.getState())); }

  getState() {
    return {
      playing: this.playing,
      frequency: this.frequency,
      waveform: this.waveform,
      binaural: this.binaural,
      isochronic: this.isochronic,
      toneVolume: this.toneVolume,
      goldenStack: this.goldenStack,
      ambient: Object.fromEntries(Object.entries(this.ambient).map(([k, v]) => [k, v.volume])),
      sessionFadeActive: !!this._sessionFadeActive,
      // Fidelity audit — resolved AudioContext sample rate (Hz). Reflects
      // the highest rate the device accepted; null when ctx isn't built yet.
      sampleRate: this.ctx ? this.ctx.sampleRate : null,
      // HF-037/038 debug hooks — surfaced so the admin fidelity panel + support
      // triage can see which platform-tuning path this session is on and whether
      // the hardware-native sample rate was probed correctly.
      androidMode: _IS_ANDROID,
      masterCeiling: _MASTER_CEILING,
      masterStartupFloor: _MASTER_STARTUP_FLOOR,
      scheduleLeadMs: _SCHEDULE_LEAD_MS,
      resumeSettleMs: _RESUME_SETTLE_MS,
      hardwareSampleRate: this._hardwareSampleRate ?? null,
      // HF-040 limiter telemetry — negative dB value showing how hard the
      // limiter is currently clamping. -0 to -0.1 dB = mostly passing;
      // < -3 dB sustained = mix is way too hot (ambient layers cranked or
      // hearing-profile boost too aggressive). `reduction` was an
      // AudioParam in older Chrome and is a plain float in current spec,
      // so we probe both shapes.
      limiterReductionDb: (() => {
        const r = this._limiter && this._limiter.reduction;
        if (r == null) return null;
        const v = (typeof r === 'number') ? r : (typeof r?.value === 'number' ? r.value : null);
        return v == null ? null : Number(v.toFixed(2));
      })(),
    };
  }

  // ---- Tone ------------------------------------------------------------------
  async start() {
    if (this.playing || this._starting) return;
    // If a background recovery is running, its own stop() has already fired
    // — a concurrent manual start() would race the recovery's follow-up
    // start(). Flip the abort flag so the recovery bails at its next async
    // boundary rather than starting a second time on top of us.
    if (this._recoveryInProgress) this._recoveryAborted = true;
    this._starting = true;
    try {
      await this._ensureCtx();
      if (this.playing) return;
      const ctx = this.ctx;

      this.toneGain = ctx.createGain();
      this.toneGain.gain.value = 0;
      this.toneGain.connect(this.master);

      // Reset master bus from any prior Smart-Fade taper. Without this, a
      // session that ended via Smart Fade leaves master.gain at 0 and the
      // next session would be silent. Set instantly inside the user-gesture
      // call stack — no ramp needed since toneGain itself fades in below.
      this.master.gain.cancelScheduledValues(ctx.currentTime);
      this.master.gain.setValueAtTime(_MASTER_CEILING, ctx.currentTime);
      this._sessionFadeActive = false;
      this._sessionFadeEndsAt = 0;

      // Isochronic gate sits between the oscillator(s) and toneGain. When
      // isochronic === 0 it acts as a unity pass-through (gain = 1). When > 0
      // a square-wave LFO drives this.gain between 0 and 1 at the pulse rate
      // for true isochronic entrainment.
      this.gateGain = ctx.createGain();
      this.gateGain.gain.value = 1;
      this.gateGain.connect(this.toneGain);

      this.osc = ctx.createOscillator();
      this.osc.type = this.waveform;
      this.osc.frequency.value = this.frequency;

      // Fidelity: schedule EVERY oscillator (base, oscR, and any φ harmonics)
      // to begin at the SAME clock time so all sines are phase-coherent at
      // t = startAt. Without this, JS execution timing between successive
      // .start() calls can land the oscillators in different 128-sample
      // render quanta, producing a small but audible phase offset that
      // subtly cancels harmonic partials.
      // HF-037: Android needs more scheduling headroom (120 ms vs 50 ms on
      // desktop/iOS) so the audio thread has time to pack the render
      // buffer without underrunning between the JS schedule call and the
      // audio-thread render deadline — the root of "choppy" playback.
      const startAt = ctx.currentTime + _SCHEDULE_LEAD_MS / 1000;

      if (this.binaural > 0) {
        const merger = ctx.createChannelMerger(2);
        const gL = ctx.createGain();
        const gR = ctx.createGain();
        this.osc.connect(gL);
        this.oscR = ctx.createOscillator();
        this.oscR.type = this.waveform;
        this.oscR.frequency.value = this.frequency + this.binaural;
        this.oscR.connect(gR);
        gL.connect(merger, 0, 0);
        gR.connect(merger, 0, 1);
        merger.connect(this.gateGain);
        this.oscR.start(startAt);
      } else {
        this.osc.connect(this.gateGain);
      }

      this.osc.start(startAt);
      this.toneGain.gain.linearRampToValueAtTime(this.toneVolume, ctx.currentTime + 0.8);

      if (this.isochronic > 0) this._spawnIsochronicLFO(this.isochronic);
      if (this.goldenStack) this._spawnPhiHarmonics(startAt);

      // Flip playing BEFORE the _pendingAmbient restore — setAmbient's
      // "stopped" branch checks this.playing, so the restore loop would
      // otherwise route every restored layer back into _pendingAmbient and
      // start() would null it on the next line, silently dropping the mix
      // the user paused with. (Caught by iter-28 testing agent regression.)
      this.playing = true;

      // Pause→Resume: restore the ambient mix that was active when the user
      // last hit Stop / paused. Cleared after restore so subsequent stops
      // re-snapshot a fresh state.
      if (this._pendingAmbient) {
        Object.entries(this._pendingAmbient).forEach(([kind, vol]) => {
          this.setAmbient(kind, vol);
        });
        this._pendingAmbient = null;
      }

      // Background-audio sink: start the hidden <audio> element NOW (we're
      // still inside the user-gesture call stack since start() is invoked
      // from a tap). This is what keeps audio alive when the screen locks.
      if (this._sinkEl) {
        try {
          const p = this._sinkEl.play();
          if (p && typeof p.catch === 'function') p.catch(() => { /* user-gesture missing — silent */ });
        } catch (e) { _warn('audio', e); }
      }
      // Bind Media Session handlers + push current metadata so lock-screen
      // controls show "Now playing: 528 Hz · Love · Solarisound".
      this._bindMediaSession();
      this._updateMediaSession();
      // Re-acquire wake lock if user previously opted-in (e.g., after stop→start).
      if (this._wakeLockWanted) this.requestWakeLock();

      this._emit();
      // Post-start health check (Issue 2 mitigation) — silently restarts the
      // session if the audio graph is broken after 500ms. Fire-and-forget.
      this._postStartHealthCheck().catch((e) => _warn('audio.health', e));
    } finally {
      this._starting = false;
    }
  }

  // Spawn the golden-ratio harmonic tones (φ¹ and φ²) at mathematically
  // exact 1/φ and 1/φ² amplitude ratios so the harmonic series preserves
  // its self-similar Fibonacci-adjacent spectrum. Base:φ¹:φ² = 1 : 0.6180 : 0.3820.
  //
  // `startAt` (optional): if provided, all φ oscillators begin at that
  // exact clock time — the same instant the base oscillator starts —
  // guaranteeing phase-coherent initialization across the whole stack. Pass
  // no argument to start "now" (used when Golden Stack is toggled on
  // mid-session, where phase alignment with the already-running base isn't
  // recoverable anyway).
  //
  // All three amplitudes (base implicit at 1.0, and the two φ layers) are
  // scaled by the SAME HEADROOM factor so their mutual ratios are preserved
  // exactly — no compression, no limiting, no per-layer gain reduction.
  _spawnPhiHarmonics(startAt) {
    const ctx = this.ctx;
    const PHI = 1.6180339887498948;  // (1 + √5) / 2
    const INV_PHI = 1 / PHI;          // ≈ 0.6180339887
    const INV_PHI2 = INV_PHI * INV_PHI; // ≈ 0.3819660113
    const HEADROOM = 0.5;
    const baseAmp = HEADROOM;
    const levels = [
      { mult: PHI, amp: INV_PHI * HEADROOM },
      { mult: PHI * PHI, amp: INV_PHI2 * HEADROOM },
    ];
    try {
      if (!this._goldenBaseGain) {
        const gBase = ctx.createGain();
        gBase.gain.value = baseAmp;
        if (this.binaural > 0) {
          this.toneGain.gain.cancelScheduledValues(ctx.currentTime);
          this.toneGain.gain.setValueAtTime(this.toneVolume * baseAmp, ctx.currentTime);
          this._goldenBaseGain = 'via-tone-gain';
        } else {
          try { this.osc.disconnect(); } catch (_) {}
          this.osc.connect(gBase);
          gBase.connect(this.gateGain || this.toneGain);
          this._goldenBaseGain = gBase;
        }
      }
    } catch (e) { _warn('audio', e); }
    levels.forEach(({ mult, amp }) => {
      const osc = ctx.createOscillator();
      osc.type = this.waveform;
      const f = this.frequency * mult;
      osc.frequency.value = f > 4000 ? f / 2 : f;
      const g = ctx.createGain();
      g.gain.value = 0;
      osc.connect(g).connect(this.gateGain || this.toneGain);
      // Phase-coherent start: honour the caller's shared startAt if given.
      if (typeof startAt === 'number' && startAt > ctx.currentTime) {
        osc.start(startAt);
      } else {
        osc.start();
      }
      g.gain.linearRampToValueAtTime(amp, ctx.currentTime + 1.0);
      this.phiOscs.push({ osc, gain: g, mult });
    });
  }

  _killPhiHarmonics(fade = 0.4) {
    if (!this.ctx || this.phiOscs.length === 0) return;
    const ctx = this.ctx;
    const local = this.phiOscs;
    this.phiOscs = [];
    local.forEach(({ gain }) => {
      gain.gain.cancelScheduledValues(ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + fade);
    });
    // Restore the base oscillator's headroom scaling to unity so a solo tone
    // plays at its natural amplitude again after Golden Stack is disabled.
    try {
      if (this._goldenBaseGain === 'via-tone-gain') {
        // Binaural path — restore toneGain to the user's tone-volume target.
        this.toneGain.gain.cancelScheduledValues(ctx.currentTime);
        this.toneGain.gain.linearRampToValueAtTime(this.toneVolume, ctx.currentTime + fade);
      } else if (this._goldenBaseGain && this._goldenBaseGain.gain) {
        // Mono path — ramp the inserted base-attenuation gain up to unity
        // then disconnect it and reconnect the oscillator directly.
        const gBase = this._goldenBaseGain;
        gBase.gain.cancelScheduledValues(ctx.currentTime);
        gBase.gain.linearRampToValueAtTime(1.0, ctx.currentTime + fade);
        setTimeout(() => {
          try {
            this.osc.disconnect();
            this.osc.connect(this.gateGain || this.toneGain);
            gBase.disconnect();
          } catch (e) { _warn('audio', e); }
        }, (fade + 0.05) * 1000);
      }
    } catch (e) { _warn('audio', e); }
    this._goldenBaseGain = null;
    setTimeout(() => {
      local.forEach(({ osc, gain }) => {
        try { osc.stop(); osc.disconnect(); gain.disconnect(); } catch (e) { _warn('audio', e); }
      });
    }, (fade + 0.05) * 1000);
  }

  // Isochronic: drive gateGain.gain between 0 and 1 at `hz` via a square LFO
  // + DC offset (so the param sees a 0..1 signal, not -1..1). Cancels the
  // constant 1.0 baseline by setting gain.value=0 before connecting.
  _spawnIsochronicLFO(hz, opts = {}) {
    if (!this.ctx || !this.gateGain) return;
    this._killIsochronicLFO(0); // idempotent
    const ctx = this.ctx;
    const fadeInMs = Math.max(0, Number(opts.fadeInMs) || 0);
    const lfo = ctx.createOscillator();
    lfo.type = 'square';
    lfo.frequency.value = Math.max(0.5, Math.min(40, hz));
    // Soft-edge the square-wave gate. A hard square wave produces
    // instantaneous 0→1 gain jumps that click audibly, and those clicks
    // get amplified by the MediaStream → hidden <audio> pipeline while
    // the phone is screen-locked — sounding like an "unwanted pulsating"
    // artifact even at low isochronic rates. A lowpass at 120 Hz softens
    // each edge to ~3–5 ms (inaudible as a click) while keeping the
    // perceptual pulse rhythm intact for entrainment.
    const smoother = ctx.createBiquadFilter();
    smoother.type = 'lowpass';
    smoother.frequency.value = 120;
    smoother.Q.value = 0.5;
    const scale = ctx.createGain();
    scale.gain.value = 0.5; // shape into ±0.5
    const offset = ctx.createConstantSource();
    offset.offset.value = 0.5; // shift to 0..1
    // fadeIn: a summing GainNode that both the (smoothed) LFO and the DC
    // offset feed into BEFORE reaching gateGain.gain. Its own .gain is
    // ramped from 0 → 1 over `fadeInMs` when the LFO is being rebuilt on
    // resume, so the reconstituted pulse ramps in smoothly instead of
    // cold-starting at whatever square-wave phase the LFO happens to be
    // at. When fadeInMs === 0 (normal setIsochronic path), gain.value = 1
    // and this node is a unity pass-through — no runtime cost.
    const fadeIn = ctx.createGain();
    if (fadeInMs > 0) {
      fadeIn.gain.setValueAtTime(0.0001, ctx.currentTime);
      fadeIn.gain.linearRampToValueAtTime(1.0, ctx.currentTime + fadeInMs / 1000);
    } else {
      fadeIn.gain.value = 1.0;
    }
    // Clear the static baseline (was 1.0) so the LFO + offset are the only drivers.
    this.gateGain.gain.cancelScheduledValues(ctx.currentTime);
    this.gateGain.gain.setValueAtTime(0, ctx.currentTime);
    lfo.connect(smoother).connect(scale).connect(fadeIn);
    offset.connect(fadeIn);
    // fadeIn is an AudioNode whose output must drive an AudioParam
    // (gateGain.gain). Web Audio permits node → AudioParam connections.
    fadeIn.connect(this.gateGain.gain);
    lfo.start();
    offset.start();
    this.isoLfo = lfo;
    this.isoSmoother = smoother;
    this.isoScale = scale;
    this.isoOffset = offset;
    this.isoFadeIn = fadeIn;
  }

  _killIsochronicLFO(restoreBaseline = 1) {
    if (!this.ctx) return;
    const ctx = this.ctx;
    [this.isoLfo, this.isoSmoother, this.isoScale, this.isoOffset, this.isoFadeIn].forEach((n) => {
      if (!n) return;
      try { if (typeof n.stop === 'function') n.stop(); } catch (e) { _warn('audio', e); }
      try { n.disconnect(); } catch (e) { _warn('audio', e); }
    });
    this.isoLfo = null;
    this.isoSmoother = null;
    this.isoScale = null;
    this.isoOffset = null;
    this.isoFadeIn = null;
    if (this.gateGain && restoreBaseline) {
      this.gateGain.gain.cancelScheduledValues(ctx.currentTime);
      this.gateGain.gain.setValueAtTime(restoreBaseline, ctx.currentTime);
    }
  }

  // Public: set isochronic pulse rate in Hz. 0 disables. Hot-swaps the LFO
  // without rebuilding the audio graph (no glitches mid-session).
  setIsochronic(hz) {
    const v = Math.max(0, Math.min(40, Number(hz) || 0));
    this.isochronic = v;
    if (this.playing && this.gateGain) {
      if (v <= 0) {
        this._killIsochronicLFO(1);
      } else if (this.isoLfo) {
        this.isoLfo.frequency.setTargetAtTime(v, this.ctx.currentTime, 0.05);
      } else {
        this._spawnIsochronicLFO(v);
      }
    }
    this._emit();
  }

  stop() {
    // If a background recovery rebuild is mid-flight, its follow-up start()
    // would fight this stop(). Flip the abort flag so the recovery bails at
    // its next async boundary and the user's stop intent wins.
    if (this._recoveryInProgress) this._recoveryAborted = true;
    const ctx = this.ctx;
    // Snapshot the currently-playing ambient volumes BEFORE we fade them out — a
    // subsequent start() (i.e., Pause → Play) will restore exactly this mix so
    // soundscapes resume seamlessly. Cleared by start() after restore, or by
    // selectSoundscape / stopAllAmbient when the user explicitly resets.
    if (ctx) {
      const snap = {};
      Object.entries(this.ambient).forEach(([k, a]) => {
        if (a && a.volume > 0.001) snap[k] = a.volume;
      });
      this._pendingAmbient = Object.keys(snap).length > 0 ? snap : null;

      // Fade every active ambient layer to 0 smoothly so the volume "dials down"
      // before playback fully stops — fires whether or not the tone is playing
      // (e.g., user paused the tone earlier, ambient is still humming).
      Object.values(this.ambient).forEach((a) => {
        if (a && a.gain && a.volume > 0.001) {
          a.gain.gain.cancelScheduledValues(ctx.currentTime);
          a.gain.gain.setValueAtTime(a.gain.gain.value, ctx.currentTime);
          a.gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.8);
          a.volume = 0;
        }
      });
    }

    if (!this.playing) {
      this._emit();
      return;
    }

    if (this.toneGain) {
      this.toneGain.gain.cancelScheduledValues(ctx.currentTime);
      this.toneGain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.8);
    }
    // Clear any in-flight Smart Fade — master gain may be mid-taper. We don't
    // restore it here because the next start() will reset master to 1, and
    // anything between now and then should stay silent.
    this._sessionFadeActive = false;
    this._sessionFadeEndsAt = 0;
    // Clear the persisted timer end so a fresh remount doesn't restore a
    // stale countdown after the user has stopped the session.
    this.sessionEndAt = 0;
    // Capture the CURRENT oscillators locally and null the instance refs immediately
    // so that a subsequent start() (within the cleanup window) doesn't get its
    // brand-new oscillator clobbered when the cleanup timeout fires.
    const oscsLocal = [this.osc, this.oscR].filter(Boolean);
    // Same discipline for gateGain — a stale cleanup disconnecting the new
    // start()'s gateGain was the root of the "player shows as playing but no
    // sound after switching frequencies" bug. Capture-then-compare-by-ref.
    const gateGainLocal = this.gateGain;
    this.osc = null;
    this.oscR = null;
    this.gateGain = null;
    this._killPhiHarmonics(0.8);
    this._killIsochronicLFO(0); // tear down LFO; gateGain disconnected via timeout below
    setTimeout(() => {
      oscsLocal.forEach((o) => { try { o.stop(); o.disconnect(); } catch (e) { _warn('audio', e); } });
      // Only disconnect the OLD gateGain — a fresh start() may already have
      // assigned a new this.gateGain by now, and we must NOT touch that.
      if (gateGainLocal) {
        try { gateGainLocal.disconnect(); } catch (e) { _warn('audio', e); }
      }
      // Pause the background-audio sink AFTER the fade completes so we don't
      // cut off the tail. Leaving it playing forever would also work, but it
      // would keep the OS media indicator lit even when the user stopped.
      if (this._sinkEl && !this.playing) {
        try { this._sinkEl.pause(); } catch (e) { _warn('audio', e); }
      }
    }, 850);
    this.playing = false;
    // Reflect stopped state to the lock-screen / OS controls + drop wake lock.
    if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
      try { navigator.mediaSession.playbackState = 'paused'; } catch (e) { _warn('audio', e); }
    }
    if (this._wakeLock) {
      // Release the OS sentinel but PRESERVE the user's "wanted" preference
      // so the next start() re-acquires automatically.
      try { this._wakeLock.release(); } catch (e) { _warn('audio', e); }
      this._wakeLock = null;
    }
    this._emit();
  }

  toggle() { this.playing ? this.stop() : this.start(); }

  // ---- Robustness: ensureRunning, health check, visibility handling ---------

  // Ramp master.gain from 0 → ceiling over ~50 ms (desktop/iOS) or after a
  // 30 ms settle window (Android). Called whenever the AudioContext
  // transitions back to 'running' after a suspend / interrupt. Masks the
  // audible pop/discontinuity at the moment the OS restores the audio thread
  // — the pop is present whether or not isochronic pulsing is active (a plain
  // sine tone or any ambient layer produces a smaller but still audible
  // artifact on resume). Idempotent: safe to call repeatedly.
  //
  // HF-038: on Android we DEFER the ramp scheduling by `_RESUME_SETTLE_MS`
  // (~30 ms) because the audio clock advances in a small burst as ctx.state
  // flips to 'running'. Starting a linear ramp inside that burst was
  // producing the initial "pop / stutter" users heard right after unlock.
  // Waiting one render quantum + driver commit slack puts the ramp origin
  // on a stable clock.
  _rampMasterOnResume() {
    if (!this.ctx || !this.master) return;
    const schedule = () => {
      if (!this.ctx || !this.master) return;
      try {
        const t = this.ctx.currentTime;
        const g = this.master.gain;
        g.cancelScheduledValues(t);
        // Snap to 0 first so the ramp origin is deterministic. A tiny epsilon
        // avoids setValueAtTime(exactly-zero) edge cases on some engines.
        g.setValueAtTime(0.0001, t);
        // 50 ms linearRampToValueAtTime — audibly smooth on all browsers we
        // support (Web Audio's setTarget never fully reaches the target,
        // which would leave a subtle static attenuation on the master bus).
        g.linearRampToValueAtTime(_MASTER_CEILING, t + 0.05);
      } catch (_) { /* graceful */ }
    };
    if (_RESUME_SETTLE_MS > 0) {
      setTimeout(schedule, _RESUME_SETTLE_MS);
    } else {
      schedule();
    }
  }

  // Consolidated resume reconciler. Both entry points — the AudioContext's
  // own 'statechange' → 'running' event (mobile screen-unlock, primary) and
  // the Dashboard's visibilitychange DOM listener (desktop fallback) — call
  // through here. A single debounced timer guarantees the isochronic LFO
  // is never rebuilt twice per unlock, no matter which path fires first
  // or in what order.
  _scheduleResumeReconciliation() {
    clearTimeout(this._resumeReconcileT);
    this._resumeReconcileT = setTimeout(() => {
      if (!this.playing || !this.gateGain) {
        // If playback was fully stopped between suspend and resume, drop
        // the frozen-iso flag so a subsequent play doesn't try to rebuild
        // stale rate state.
        this._isoFrozen = false;
        this._isoRateBeforeHide = 0;
        return;
      }
      if (this._isoFrozen && this._isoRateBeforeHide > 0) {
        this._isoFrozen = false;
        this.isochronic = this._isoRateBeforeHide;
        this._isoRateBeforeHide = 0;
        try {
          // Short fade-in on rebuild so the first pulse's leading edge
          // ramps in over ~40 ms instead of cold-starting from silence
          // at whatever phase the LFO happens to be at. Prevents the
          // "phantom first pulse" artifact users notice on unlock.
          this._spawnIsochronicLFO(this.isochronic, { fadeInMs: 40 });
        } catch (e) { _warn('audio.isoResume', e); }
      }
    }, 220);
  }

  // Fires whenever ctx.state changes. On mobile screen-lock the OS transitions
  // us to 'suspended' *before* any DOM event (visibilitychange / pagehide /
  // blur) can reach us. If we don't tear down the isochronic LFO in the same
  // synchronous tick, when the OS later resumes the audio thread the LFO's
  // internal square-wave phase is stale and its FIRST output cycle after
  // resume produces an audible click / phantom pulse — the exact "isochronic
  // artifact on screen lock" the user reported. Handling it here (rather than
  // via visibilitychange) gets us in ahead of the OS.
  _onCtxStateChange() {
    if (!this.ctx) return;
    const st = this.ctx.state;
    if (st === 'suspended' || st === 'interrupted') {
      // Snap the gate to unity SYNCHRONOUSLY via direct .value assignment —
      // NOT via setValueAtTime, which requires a running context and would
      // silently no-op here. Then hard-disconnect the LFO chain so no phase
      // state persists across the suspension.
      if (this.playing && this.isochronic > 0 && !this._isoFrozen) {
        this._isoRateBeforeHide = this.isochronic;
        try {
          [this.isoLfo, this.isoSmoother, this.isoScale, this.isoOffset, this.isoFadeIn].forEach((n) => {
            if (!n) return;
            try { n.disconnect(); } catch (_) {}
            try { if (typeof n.stop === 'function') n.stop(); } catch (_) {}
          });
        } catch (e) { _warn('audio.freeze', e); }
        this.isoLfo = null;
        this.isoSmoother = null;
        this.isoScale = null;
        this.isoOffset = null;
        this.isoFadeIn = null;
        if (this.gateGain) {
          try { this.gateGain.gain.value = 1; } catch (_) {}
        }
        this._isoFrozen = true;
      }
    } else if (st === 'running') {
      // Whole-graph master fade-in masks the resume discontinuity even when
      // isochronic pulsing is off (a plain sine tone or ambient layer still
      // clicks slightly on ctx wake). Immediate, not debounced — the pop
      // is at the resume instant.
      this._rampMasterOnResume();
      // Iso rebuild goes through the shared debounced reconciler so DOM
      // visibilitychange + statechange never rebuild the LFO twice.
      this._scheduleResumeReconciliation();
    }
  }

  // Callable from any user-gesture path to defensively re-arm the audio ctx.
  // Handles the mobile browser edge case where an implicit suspension (screen
  // lock, tab background) leaves ctx.state === 'suspended' even after the
  // MediaStream keep-alive sink was created. Silent no-op when ctx is already
  // running, so it's safe to call on every play/resume site.
  async ensureRunning() {
    if (!this.ctx) return;
    if (this.ctx.state === 'running') return;
    try {
      await this.ctx.resume();
    } catch (e) { _warn('audio.ensureRunning', e); }
    // Nudge the hidden <audio> sink back into play state — some Android
    // Chrome builds pause it on lock even when we don't ask them to.
    if (this._sinkEl && this._sinkEl.paused && this.playing) {
      try { await this._sinkEl.play(); } catch (e) { _warn('audio.sinkPlay', e); }
    }
  }

  // Called by the Dashboard's visibilitychange listener when the page becomes
  // hidden (screen lock / tab switch / app-switch on mobile). This is a
  // BACKUP path — the primary defence against pulse artifacts is now the
  // AudioContext statechange handler above, which fires ahead of any DOM
  // event on mobile. But desktop browsers do NOT always transition ctx to
  // 'suspended' on tab hide, so we keep this belt-and-suspenders trigger.
  freezeIsochronicForBackground() {
    if (!this.ctx || !this.playing) return;
    if (this.isochronic <= 0) return; // nothing to freeze
    if (this._isoFrozen) return;      // idempotent — statechange may have fired first
    this._isoRateBeforeHide = this.isochronic;
    this._killIsochronicLFO(1);
    this._isoFrozen = true;
  }

  // Called when the tab is visible again and the ctx has been resumed.
  // Belt-and-suspenders for browsers where AudioContext.statechange does not
  // fire on unhide (desktop Firefox and some Android Chrome builds).
  // Delegates to the shared debounced reconciler so no matter which path
  // wins the race (this one or _onCtxStateChange('running')) the isochronic
  // LFO is rebuilt exactly once. Also runs the 50ms master fade-in — cheap,
  // idempotent, and needed on the browsers where statechange doesn't fire.
  resumeIsochronicAfterBackground() {
    if (!this.ctx) return;
    // If ctx is still suspended, defer — the eventual statechange handler
    // will do it. We can't schedule audio ramps against a suspended clock.
    if (this.ctx.state !== 'running') return;
    this._rampMasterOnResume();
    if (!this._isoFrozen) return;
    this._scheduleResumeReconciliation();
  }

  // Post-start audio health check. Fires ~500ms after any start() to detect
  // the "shows as playing but produces no sound" failure mode. If any of the
  // graph invariants are broken, we do a silent stop → start restart at the
  // SAME frequency/settings without user action.
  //
  // Invariants (all must hold, else we rebuild):
  //   • ctx exists and is 'running'
  //   • gateGain still connected (numberOfInputs > 0 is a weak proxy, but the
  //     more reliable signal is: gateGain === null while this.playing → broken)
  //   • at least one oscillator is bound to this instance
  //   • master.gain is > 0.001 — a stuck-at-zero master bus is silent even
  //     though everything else looks connected (happens if a resume-ramp
  //     gets interrupted mid-fade)
  //   • toneGain.gain is > 0.001 whenever the user's intended toneVolume > 0
  //     — catches the failed ramp case where linearRampToValueAtTime never
  //     climbed off zero
  //   • the background <audio> sink is not paused — a paused sink silently
  //     kills background/lock-screen playback even when the graph is fine
  //
  // Concurrency: A `_recoveryInProgress` guard blocks two health checks from
  // running rebuild logic at the same time. Any manual stop()/start() during
  // recovery flips `_recoveryAborted`, and the recovery function bails at
  // each async boundary so the user's intent always wins.
  async _postStartHealthCheck() {
    // Give the audio graph a brief settle so linearRampToValueAtTime has
    // moved toneGain off zero before we probe.
    await new Promise((r) => setTimeout(r, 500));
    if (!this.playing) return;
    if (this._recoveryInProgress) return; // don't stack recoveries
    const wantAudible = (this.toneVolume || 0) > 0.001;
    const masterVal = (this.master && typeof this.master.gain?.value === 'number')
      ? this.master.gain.value : null;
    const toneVal = (this.toneGain && typeof this.toneGain.gain?.value === 'number')
      ? this.toneGain.gain.value : null;
    const sinkPaused = !!(this._sinkEl && this._sinkEl.paused);
    const broken =
      !this.ctx ||
      this.ctx.state !== 'running' ||
      !this.gateGain ||
      !this.osc ||
      (masterVal !== null && masterVal < 0.001) ||
      (wantAudible && toneVal !== null && toneVal < 0.001) ||
      sinkPaused;
    if (!broken) return;
    _warn('audio.health', new Error(
      `broken audio graph after start — ctx=${this.ctx && this.ctx.state}, ` +
      `gateGain=${!!this.gateGain}, osc=${!!this.osc}, ` +
      `master=${masterVal}, tone=${toneVal}, wantAudible=${wantAudible}, ` +
      `sinkPaused=${sinkPaused}. Rebuilding silently.`
    ));
    // Cheap sink revive: if the ONLY problem is the sink being paused (graph
    // otherwise fine and ctx running), try to un-pause without tearing down.
    // The sink can pause on Android Chrome without breaking the rest of the
    // graph — a full rebuild for that alone is overkill and audible.
    const onlySinkIssue = sinkPaused
      && this.ctx && this.ctx.state === 'running'
      && this.gateGain && this.osc
      && (masterVal === null || masterVal >= 0.001)
      && (!wantAudible || toneVal === null || toneVal >= 0.001);
    if (onlySinkIssue && this._sinkEl) {
      try { await this._sinkEl.play(); } catch (_) { /* graceful */ }
      return;
    }
    // Full rebuild path — take the recovery lock.
    this._recoveryInProgress = true;
    this._recoveryAborted = false;
    // Silent rebuild — carry current user settings forward.
    const carry = {
      frequency: this.frequency,
      waveform: this.waveform,
      binaural: this.binaural,
      toneVolume: this.toneVolume,
      goldenStack: this.goldenStack,
      isochronic: this.isochronic,
      ambientSnapshot: Object.fromEntries(
        Object.entries(this.ambient || {}).map(([k, a]) => [k, a && a.volume || 0])
      ),
    };
    try {
      this.stop();
    } catch (e) { _warn('audio.health.stop', e); }
    // Wait for the 850ms cleanup timeout to finish before restarting so we
    // don't collide with our own stale disconnect. Manual stop/start during
    // this window flips _recoveryAborted — we bail rather than fight the
    // user's intent.
    await new Promise((r) => setTimeout(r, 900));
    if (this._recoveryAborted) {
      this._recoveryInProgress = false;
      this._recoveryAborted = false;
      return;
    }
    // Restore settings + pending ambient so start() reproduces the mix.
    this.frequency = carry.frequency;
    this.waveform = carry.waveform;
    this.binaural = carry.binaural;
    this.toneVolume = carry.toneVolume;
    this.goldenStack = carry.goldenStack;
    this.isochronic = carry.isochronic;
    if (carry.ambientSnapshot && Object.values(carry.ambientSnapshot).some((v) => v > 0.001)) {
      this._pendingAmbient = carry.ambientSnapshot;
    }
    try {
      if (!this._recoveryAborted) await this.start();
    } catch (e) { _warn('audio.health.restart', e); }
    this._recoveryInProgress = false;
    this._recoveryAborted = false;
  }


  setFrequency(hz) {
    this.frequency = hz;
    const applyFreqSchedules = () => {
      if (!this.ctx) return;
      const now = this.ctx.currentTime;
      if (this.osc) this.osc.frequency.setTargetAtTime(hz, now, 0.05);
      if (this.oscR) this.oscR.frequency.setTargetAtTime(hz + this.binaural, now, 0.05);
      this.phiOscs.forEach(({ osc, mult }) => {
        const f = hz * mult;
        const safe = f > 4000 ? f / 2 : f;
        osc.frequency.setTargetAtTime(safe, now, 0.05);
      });
    };
    // If the user is switching frequencies mid-session AND the audio thread
    // has silently suspended (mobile screen-lock lag, background tab throttle,
    // Android Chrome's opportunistic pause), scheduling setTargetAtTime
    // against a suspended clock has no audible effect — the player would
    // appear to change frequency but the ear hears nothing. Resume first,
    // then apply the schedules against the freshly-running clock so the new
    // tone actually plays. When ctx is already running (the vast majority of
    // switches), this fast-path is a no-op.
    if (this.playing && this.ctx && this.ctx.state !== 'running') {
      this.ensureRunning().then(applyFreqSchedules).catch(() => applyFreqSchedules());
    } else {
      applyFreqSchedules();
    }
    this._emit();
  }

  // ---- Baseline tone -----------------------------------------------------
  // Independent low-volume oscillator that runs IN PARALLEL with the main
  // session — used by the onboarding transition flow to gently layer a
  // soothing 432 Hz under whatever the user already chose. Connects directly
  // to `master` so it sits underneath the same EQ chain as the rest of the
  // mix (when calibrated). Call setBaseline(null) or setBaseline(0,0) to
  // fade it out cleanly.
  setBaseline(hz, volume = 0.06) {
    // Tear down any prior baseline first so calling this multiple times is
    // idempotent and never leaks oscillators.
    if (this._baselineOsc || this._baselineGain) {
      const now = this.ctx ? this.ctx.currentTime : 0;
      try {
        if (this._baselineGain && this.ctx) {
          this._baselineGain.gain.cancelScheduledValues(now);
          this._baselineGain.gain.linearRampToValueAtTime(0, now + 0.6);
        }
        if (this._baselineOsc && this.ctx) this._baselineOsc.stop(now + 0.7);
      } catch (e) { /* graceful */ }
      const oldOsc = this._baselineOsc;
      const oldGain = this._baselineGain;
      this._baselineOsc = null;
      this._baselineGain = null;
      setTimeout(() => {
        try { oldOsc && oldOsc.disconnect(); } catch (e) { /* */ }
        try { oldGain && oldGain.disconnect(); } catch (e) { /* */ }
      }, 900);
    }
    if (!hz || !volume) return;
    // Lazy-build the AudioContext so the baseline can be requested before
    // the user has tapped play.
    this._ensureCtx().then((ctx) => {
      try {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = hz;
        const g = ctx.createGain();
        g.gain.value = 0;
        osc.connect(g).connect(this.master);
        osc.start();
        const now = ctx.currentTime;
        const v = Math.max(0, Math.min(0.25, volume)); // safety cap
        g.gain.linearRampToValueAtTime(v, now + 1.5); // slow swell so it feels organic
        this._baselineOsc = osc;
        this._baselineGain = g;
      } catch (e) { _warn('audio', e); }
    });
  }

  setWaveform(w) {
    this.waveform = w;
    if (this.osc) this.osc.type = w;
    if (this.oscR) this.oscR.type = w;
    this.phiOscs.forEach(({ osc }) => { osc.type = w; });
    this._emit();
  }

  setBinaural(offset) {
    const oldOffset = this.binaural;
    this.binaural = offset;
    // Only rebuild the graph when the binaural TOPOLOGY changes (i.e., turning the
    // right-ear oscillator on or off). For pure offset tweaks while already binaural,
    // just retune the right oscillator live — no stop/start that would interrupt audio.
    const topologyChange = (oldOffset === 0) !== (offset === 0);
    if (this.playing && topologyChange) {
      this.stop();
      setTimeout(() => this.start(), 460);
    } else if (this.oscR) {
      this.oscR.frequency.setTargetAtTime(this.frequency + offset, this.ctx.currentTime, 0.05);
    }
    this._emit();
  }

  setToneVolume(v) {
    this.toneVolume = v;
    if (this.toneGain) {
      // Fidelity: when Golden Stack + binaural are BOTH active, the toneGain
      // path also carries the golden headroom (0.5) so all 3 layers preserve
      // exact 1 : 1/φ : 1/φ² ratios without clipping. Otherwise unity.
      const headroom = (this._goldenBaseGain === 'via-tone-gain') ? 0.5 : 1.0;
      this.toneGain.gain.setTargetAtTime(v * headroom, this.ctx.currentTime, 0.05);
    }
    this._emit();
  }

  // Gracefully ramp tone + all ambient gains down to 0 over `seconds`.
  // Legacy per-source fade. Retained for any caller that wants source-level
  // fading; the preferred path for session-end smart fade is
  // beginSessionFade() below, which operates on the master bus so the
  // user's layered mix balance is automatically preserved.
  fadeOutAll(seconds = 60) {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const ramp = (param) => {
      const cur = param.value;
      param.cancelScheduledValues(t);
      param.setValueAtTime(cur, t);
      param.linearRampToValueAtTime(0, t + seconds);
    };
    if (this.toneGain) ramp(this.toneGain.gain);
    Object.values(this.ambient).forEach((a) => { if (a.gain) ramp(a.gain.gain); });
  }

  // ---- Smart Session Fade ----------------------------------------------------
  // Master-bus fade for the last N seconds of a timed session. By scheduling
  // a single linearRampToValueAtTime on this.master.gain we automatically
  // preserve the relative balance of every downstream source (tone, golden
  // harmonics, isochronic gate, all ambient layers). Idempotent: a second
  // call is a no-op so the timer's per-second tick can safely poll-fire it.

  beginSessionFade(seconds) {
    if (!this.ctx || !this.master) return;
    if (this._sessionFadeActive) return;
    const dur = Math.max(0.5, Number(seconds) || 0);
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const cur = this.master.gain.value;
    this.master.gain.cancelScheduledValues(t);
    this.master.gain.setValueAtTime(cur, t);
    this.master.gain.linearRampToValueAtTime(0, t + dur);
    this._sessionFadeActive = true;
    this._sessionFadeEndsAt = t + dur;
    this._emit();
  }

  // Abort an in-flight smart fade. `restoreInstantly=true` snaps the master
  // back to 1.0 immediately (used by stop() during cleanup where audio is
  // already being killed). The default 0.4s ramp-back is the smooth path used
  // when the user changes settings mid-fade and we want to undo the taper.
  cancelSessionFade(restoreInstantly = false) {
    if (!this.ctx || !this.master) return;
    if (!this._sessionFadeActive) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    this.master.gain.cancelScheduledValues(t);
    if (restoreInstantly) {
      this.master.gain.setValueAtTime(_MASTER_CEILING, t);
    } else {
      this.master.gain.setValueAtTime(this.master.gain.value, t);
      this.master.gain.linearRampToValueAtTime(_MASTER_CEILING, t + 0.4);
    }
    this._sessionFadeActive = false;
    this._sessionFadeEndsAt = 0;
    this._emit();
  }

  isFading() { return !!this._sessionFadeActive; }

  setGoldenStack(on) {
    this.goldenStack = !!on;
    if (this.playing) {
      if (this.goldenStack && this.phiOscs.length === 0) this._spawnPhiHarmonics();
      if (!this.goldenStack && this.phiOscs.length > 0) this._killPhiHarmonics(0.4);
    }
    this._emit();
  }

  // ---- Ambient ---------------------------------------------------------------
  _makeNoise() {
    const ctx = this.ctx;
    const bufferSize = 2 * ctx.sampleRate;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let lastOut = 0;
    // Pink-ish noise for warmth. First-pass generation writes low-amplitude
    // (~±0.15) samples via a simple 1-pole low-pass on white noise.
    let peak = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      data[i] = (lastOut + 0.02 * white) / 1.02;
      lastOut = data[i];
      const abs = Math.abs(data[i]);
      if (abs > peak) peak = abs;
    }
    // HF-040 peak-normalisation. The previous `*= 3.5` static boost pushed
    // noise samples to ~±0.5 (peak-dependent), and once several ambient
    // layers ran into the same summing bus the combined signal easily
    // exceeded unity — the largest source of Android distortion at
    // steady state. Peak-normalise so every generated buffer lands at
    // exactly ±0.35 (comfortably below unity for the tone/harmonics sum
    // and safe for the master limiter). Guards against divide-by-zero
    // when the random walk somehow produced a silent buffer.
    const TARGET_PEAK = 0.35;
    const scale = peak > 1e-6 ? (TARGET_PEAK / peak) : 1;
    for (let i = 0; i < bufferSize; i++) {
      data[i] *= scale;
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    return src;
  }

  _buildAmbient(kind, startAt) {
    const ctx = this.ctx;
    const src = this._makeNoise();
    const filter = ctx.createBiquadFilter();
    const userGain = ctx.createGain();
    userGain.gain.value = 0;
    let modGain = null;
    let lfo = null;

    // All time-based starts use the unified `startAt` so every layer is sample-aligned.
    const t0 = startAt || ctx.currentTime;
    if (kind === 'rain') {
      filter.type = 'highpass'; filter.frequency.value = 1400; filter.Q.value = 0.5;
      src.connect(filter).connect(userGain);
    } else if (kind === 'ocean') {
      filter.type = 'lowpass'; filter.frequency.value = 600; filter.Q.value = 0.8;
      modGain = ctx.createGain(); modGain.gain.value = 0.55;
      lfo = ctx.createOscillator(); lfo.frequency.value = 0.12;
      const lfoAmp = ctx.createGain(); lfoAmp.gain.value = 0.45;
      lfo.connect(lfoAmp).connect(modGain.gain);
      lfo.start(t0);
      src.connect(filter).connect(modGain).connect(userGain);
    } else if (kind === 'forest') {
      filter.type = 'bandpass'; filter.frequency.value = 2200; filter.Q.value = 0.7;
      src.connect(filter).connect(userGain);
    } else if (kind === 'white') {
      src.connect(userGain);
    } else if (kind === 'brown') {
      filter.type = 'lowpass'; filter.frequency.value = 220; filter.Q.value = 0.7;
      src.connect(filter).connect(userGain);
    } else if (kind === 'wind') {
      filter.type = 'bandpass'; filter.frequency.value = 900; filter.Q.value = 1.4;
      modGain = ctx.createGain(); modGain.gain.value = 0.6;
      lfo = ctx.createOscillator(); lfo.frequency.value = 0.07;
      const lfoAmp = ctx.createGain(); lfoAmp.gain.value = 0.4;
      lfo.connect(lfoAmp).connect(modGain.gain);
      lfo.start(t0);
      src.connect(filter).connect(modGain).connect(userGain);
    } else if (kind === 'crickets') {
      filter.type = 'bandpass'; filter.frequency.value = 4800; filter.Q.value = 6;
      modGain = ctx.createGain(); modGain.gain.value = 0.5;
      lfo = ctx.createOscillator(); lfo.frequency.value = 7;
      const lfoAmp = ctx.createGain(); lfoAmp.gain.value = 0.45;
      lfo.connect(lfoAmp).connect(modGain.gain);
      lfo.start(t0);
      src.connect(filter).connect(modGain).connect(userGain);
    } else if (kind === 'bowls') {
      filter.type = 'bandpass'; filter.frequency.value = 380; filter.Q.value = 8;
      modGain = ctx.createGain(); modGain.gain.value = 0.7;
      lfo = ctx.createOscillator(); lfo.frequency.value = 0.05;
      const lfoAmp = ctx.createGain(); lfoAmp.gain.value = 0.3;
      lfo.connect(lfoAmp).connect(modGain.gain);
      lfo.start(t0);
      src.connect(filter).connect(modGain).connect(userGain);
    } else {
      src.connect(filter).connect(userGain);
    }

    userGain.connect(this.master);
    src.start(t0);
    return { src, filter, gain: userGain, modGain, lfo, volume: 0 };
  }

  setAmbient(kind, volume) {
    // Ambient nodes are pre-built in _ensureCtx so all 8 layers are sample-aligned.
    // If ctx isn't ready yet (very first interaction), trigger unlock — we'll catch
    // the slider movement on the next tick.
    if (!this.ctx) { this.unlock(); return; }
    if (!this.ambient[kind]) {
      // Fallback: lazily build if somehow missing (e.g., after a hot reload),
      // aligned to the existing ambient layers via the same current time.
      this.ambient[kind] = this._buildAmbient(kind, this.ctx.currentTime);
    }
    const a = this.ambient[kind];
    const v = Math.max(0, Math.min(1, Number(volume) || 0));
    a.volume = v;
    // PLAYER CONTRACT: ambient layers only become audible while the player is
    // actively playing. When the player is stopped, we record the desired mix
    // into `_pendingAmbient` so it's applied on the next start() (same channel
    // the pause→resume restore uses). Without this gate, AI prescriptions and
    // soundscape clicks could start audio while the play/pause UI says "Play",
    // and the user couldn't toggle it off from the player.
    if (!this.playing) {
      this._pendingAmbient = this._pendingAmbient || {};
      if (v > 0) this._pendingAmbient[kind] = v;
      else delete this._pendingAmbient[kind];
      // Hold the actual gain at 0 — the noise source is still running, just silent.
      try {
        a.gain.gain.cancelScheduledValues(this.ctx.currentTime);
        a.gain.gain.setValueAtTime(0, this.ctx.currentTime);
      } catch (e) { _warn('audio.setAmbient.idle', e); }
      this._emit();
      return;
    }
    const ctx = this.ctx;
    if (v === 0) {
      // Smooth fade-to-silent instead of instant cutoff — used both for slider
      // drags to 0 and for soundscape stop. The 0.8s ramp matches the tone fade
      // in stop() so multi-layer mixes wind down together.
      const current = a.gain.gain.value || 0;
      a.gain.gain.cancelScheduledValues(ctx.currentTime);
      if (current > 0.001) {
        a.gain.gain.setValueAtTime(current, ctx.currentTime);
        a.gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.8);
      } else {
        a.gain.gain.setValueAtTime(0, ctx.currentTime);
      }
    } else {
      a.gain.gain.cancelScheduledValues(ctx.currentTime);
      a.gain.gain.setTargetAtTime(v, ctx.currentTime, 0.2);
    }
    this._emit();
  }

  stopAllAmbient() {
    Object.values(this.ambient).forEach((a) => {
      try {
        if (this.ctx) a.gain.gain.setValueAtTime(0, this.ctx.currentTime);
        if (a.lfo) { try { a.lfo.stop(); a.lfo.disconnect(); } catch (e) { _warn('audio', e); } }
        a.src.stop(); a.src.disconnect();
      } catch (e) { _warn('audio', e); }
    });
    this.ambient = {};
    this._pendingAmbient = null;
    this._emit();
  }
}

const audioEngine = new AudioEngine();
// Test-only handle for E2E automation. Stripped from production bundles so
// pages served from solarisound.com never expose the raw engine on window.
// Preview + local dev keep it so Playwright can probe internal state.
if (typeof window !== 'undefined' && typeof process !== 'undefined'
    && process.env && process.env.NODE_ENV !== 'production') {
  try { window.__audioEngine = audioEngine; } catch (_) { /* noop */ }
}
export default audioEngine;
